"use client";

import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, EffectCoverflow } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";

import styles from "./BeyondScreenCarouselComponentHomeNew.module.scss";
import ArrowRightSymbol from "@/SvgIcons/ArrowRightSymbol";
import ArrowLeftSymbol from "@/SvgIcons/ArrowLeftSymbol";

/**
 * Coverflow gallery — keep Swiper (EffectCoverflow). Optimizations only:
 * static CDN URLs, no duplicate slides, lazy images, trim unused modules.
 */

const galleryData = [
  {
    title: "Student Orientation - Samagam",
    description:
      "Orientation session introducing learners to platforms, faculty, and academic life.",
    images: [
      "https://amityonline.com/_s/samagam_2_ecd0757f87.webp",
      "https://amityonline.com/_s/samagam_4_9552ff4b02.webp",
      "https://amityonline.com/_s/samagam_6_0a3ea86e90.webp",
      "https://amityonline.com/_s/samagam_7_fd8d550a1a.webp",
      "https://amityonline.com/_s/samagam_1_59f52cbc30.webp",
      "https://amityonline.com/_s/samagam_3_e5b335022b.webp",
      "https://amityonline.com/_s/samagam_5_024ad270c3.webp",
      "https://static.amityonline.com/_s/samagam_2_ecd0757f87.webp",
      "https://static.amityonline.com/_s/samagam_4_9552ff4b02.webp",
    ],
  },
  {
    title: "Mid-Year Meetup",
    description: "Interactive sessions fostering interactions with peers and faculty.",
    images: [
      "https://amityonline.com/_s/Mid_Year_Meetup_2_83b2773245.webp",
      "https://amityonline.com/_s/Mid_Year_Meetup_4_dfa96a86e7.webp",
      "https://amityonline.com/_s/IMG_3853_0961d683c3.webp",
      "https://amityonline.com/_s/IMG_3708_3a0d949668.webp",
      "https://amityonline.com/_s/Mid_Year_Meetup_ba7a1ea3f2.webp",
      "https://amityonline.com/_s/Mid_Year_Meetup_3_264a28f631.webp",
      "https://amityonline.com/_s/Mid_Year_Meetup_1_ac548ae8e4.webp",
      "https://static.amityonline.com/_s/Mid_Year_Meetup_2_83b2773245.webp",
      "https://static.amityonline.com/_s/Mid_Year_Meetup_4_dfa96a86e7.webp",

    ],
  },
  {
    title: "Sangathan",
    description: "Amity's annual flagship event with inter-campus sports activities.",
    images: [
      "https://amityonline.com/_s/0_G4_A2784_9d9b51f760.webp",
      "https://amityonline.com/_s/0_G4_A2899_56be1bd3cc.webp",
      "https://amityonline.com/_s/0_G4_A2853_0fd242a998.webp",
      "https://amityonline.com/_s/0_G4_A2940_cfdcd50810.webp",
      "https://amityonline.com/_s/0_G4_A2918_3d393b5e60.webp",
      "https://amityonline.com/_s/0_G4_A2883_648ced6ced.webp",
      "https://amityonline.com/_s/0_G4_A2842_acffeed8f4.webp",
      "https://static.amityonline.com/_s/0_G4_A2784_9d9b51f760.webp",
      "https://static.amityonline.com/_s/0_G4_A2899_56be1bd3cc.webp",
    ],
  },
  {
    title: "Convocation",
    description: "Formal degree distribution ceremony celebrating academic milestones.",
    images: [
      "https://amityonline.com/_s/0_G4_A0382_f1782ad9ea.webp",
      "https://amityonline.com/_s/NIL_05387_6d7afc6d29.webp",
      "https://amityonline.com/_s/NIL_05145_6cb54365db.webp",
      "https://amityonline.com/_s/0_G4_A0151_9d09c85855.webp",
      "https://amityonline.com/_s/0_G4_A9441_0d68944ad1.webp",
      "https://amityonline.com/_s/IMG_6118_d6573fc77f.webp",
      "https://amityonline.com/_s/0_G4_A1555_686e504227.webp",
      "https://static.amityonline.com/_s/0_G4_A0382_f1782ad9ea.webp",
      "https://static.amityonline.com/_s/NIL_05387_6d7afc6d29.webp",

    ],
  },
];

const BeyondScreenCarouselComponent = () => {
  const outerSwiperRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const rootRef = useRef(null);
  // Default true so in-view behavior matches today; pause only when scrolled away.
  const [isInView, setIsInView] = useState(true);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { rootMargin: "120px 0px", threshold: 0.01 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (outerSwiperRef.current && prevRef.current && nextRef.current) {
      const swiper = outerSwiperRef.current;

      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;

      swiper.navigation.destroy();
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, []);

  useEffect(() => {
    return () => {
      outerSwiperRef.current?.destroy(true, false);
    };
  }, []);

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.container}>
        <h2>
          Beyond Screens: Experience On Campus Learning With Amity Online
        </h2>
      </div>

      <div className={styles.galleryList}>
        <Swiper
          modules={[Navigation]}
          slidesPerView={1}
          loop={false}
          rewind={true}
          grabCursor={false}
          onSwiper={(swiper) => {
            outerSwiperRef.current = swiper;
          }}
          className={styles.gallerySlider}
          observer={true}
          observeParents={true}
        >
          {galleryData.map((item) => (
            <SwiperSlide key={item.title}>
              {({ isActive }) => (
                <div className={styles.galleryItem}>
                  <div className={styles.galleryCat}>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                  {isActive && (
                    <InnerSlider
                      isActive={isActive}
                      isInView={isInView}
                      images={item.images}
                      title={item.title}
                      styles={styles}
                      onComplete={() => {
                        outerSwiperRef.current?.slideNext();
                      }}
                    />
                  )}
                </div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>

        <div className={styles.navWrapper}>
          <button
            type="button"
            ref={prevRef}
            className={styles.navBtn}
            aria-label="Previous gallery"
          >
            <ArrowLeftSymbol />
          </button>

          <button
            type="button"
            ref={nextRef}
            className={styles.navBtn}
            aria-label="Next gallery"
          >
            <ArrowRightSymbol />
          </button>
        </div>
      </div>
    </div>
  );
};

const InnerSlider = ({ images, title, styles, onComplete, isActive, isInView }) => {
  const autoplayDelay = 1500;
  const swiperRef = useRef(null);

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper?.autoplay) return;
    // Same autoplay when visible; stop only offscreen (no design change in view).
    if (isActive && isInView) {
      swiper.autoplay.start();
    } else {
      swiper.autoplay.stop();
    }
  }, [isActive, isInView]);

  return (
    <Swiper
      modules={[Autoplay, EffectCoverflow]}
      effect="coverflow"
      onSwiper={(swiper) => {
        swiperRef.current = swiper;
      }}
      /* rewind = seamless restart without cloning slides (avoids duplicate <img> requests) */
      loop={false}
      rewind={true}
      autoplay={{
        delay: autoplayDelay,
        disableOnInteraction: false,
      }}
      spaceBetween={2}
      slidesPerView={5}
      grabCursor
      className={styles.innerSlider}
      onReachEnd={() => {
        if (isActive && isInView) {
          setTimeout(() => {
            onComplete();
          }, 1500);
        }
      }}
      coverflowEffect={{
        rotate: 0,
        stretch: 0,
        depth: 100,
        modifier: 2.5,
      }}
      observer={true}
      observeParents={true}
    >
      {images.map((img, index) => (
        <SwiperSlide key={`${title}-${index}`}>
          <div className={styles.galImg}>
            <img
              src={img}
              alt={title}
              /* All lazy: section is below the fold and now SSR'd — eager here would compete with LCP */
              loading="lazy"
              decoding="async"
              fetchPriority={index === 0 ? "low" : undefined}
            />
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default BeyondScreenCarouselComponent;
